<script setup lang="ts">
import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { computed } from "vue";

const props = defineProps<{
  icon: IconName
  variant?: "default" | "danger" | "add"
  text: string
  /** Grey line under the text, e.g. the current location. */
  subText?: string
  /** Unread badge at the end of the row. */
  count?: number
  /** Chevron at the very end of the row, for entries that open something. */
  chevron?: boolean
}>();

defineSlots<{
  /** Content before the chevron, e.g. the current language. */
  trailing?: () => unknown
}>();

const isDanger = computed(() => props.variant === "danger");

const tileClass = computed(() => {
  if (isDanger.value) {
    return "bg-[#FFEBEE] text-[#C62828] dark:bg-red-500/15 dark:text-red-400";
  }
  if (props.variant === "add") {
    return "border-[1.5px] border-dashed border-[#BDBDBD] bg-white text-[#4CAF50] dark:border-dark-400 dark:bg-transparent";
  }
  return "bg-[#F5F5F5] text-[#616161] dark:bg-dark-600 dark:text-gray-300";
});
</script>

<template>
  <div class="flex min-h-11 cursor-pointer items-center gap-3 px-4 py-1.5 text-sm/5 text-[#424242] hover:bg-[#FAFAFA] dark:text-gray-100 dark:hover:bg-dark-600">
    <div
      class="flex size-8 shrink-0 items-center justify-center rounded-[10px]"
      :class="tileClass"
    >
      <VIcon :name="icon" class="size-4" aria-hidden="true" />
    </div>
    <div class="flex min-w-0 flex-1 flex-col">
      <span class="truncate" :class="isDanger ? 'text-[#C62828] dark:text-red-400' : ''">{{ text }}</span>
      <span v-if="subText" class="truncate text-xs/4 text-[#757575] dark:text-gray-400">{{ subText }}</span>
    </div>
    <div
      v-if="count"
      class="flex h-5 min-w-5 items-center justify-center rounded-[10px] bg-[#4CAF50] px-1.5 text-[11px] leading-none font-semibold text-white"
      data-testid="navbar-item-count"
    >
      {{ count }}
    </div>
    <slot name="trailing" />
    <VIcon v-if="chevron" name="chevron-right" class="size-3.5 shrink-0 text-[#BDBDBD]" aria-hidden="true" />
  </div>
</template>
